import { Inspection } from '@/types/Inspection.type';
import { MasterDataState } from '@/store/slices/masterDataSlice';
import { DefectSeverity } from '@/types/DefectCategory.type';
import { DefectSeverity as DefectSeverityEnum } from '@/enums/DefectSeverity.enum';
import { InspectionResult } from '@/enums/InspectionResult.enum';
import ClientDecision from '@/enums/ClientDecision.enum.ts';
import { SamplingMethod } from '@/types/InspectionLevel';

/**
 * Calculate the overall inspection result based on defects and quantity
 */
export const calculateInspectionResult = (
  inspection: Inspection,
  masterData: MasterDataState
): InspectionResult => {
  const samplingMethod = inspection.inspectionLevel?.samplingMethod;
  if (samplingMethod === SamplingMethod.FIXED) {
    return InspectionResult.COMPLETE;
  }
  console.log('[calculateInspectionResult] inspection id ', inspection?.id);
  //No defects, return pass
  if (!inspection.qcDefects || inspection.qcDefects.length === 0) {
    console.log('[calculateInspectionResult] no defects');
    return InspectionResult.PASS;
  }
  // if client decision is set, do not calculate the result and return the client decision
  if (inspection.clientDecisionId) {
    console.log('[calculateInspectionResult] client decision id ', inspection.clientDecisionId);
    const clientDecision = masterData.clientDecisions.find(
      (d) => d.id === inspection.clientDecisionId
    );
    if (clientDecision) {
      console.log('[calculateInspectionResult] client decision ', clientDecision);
      // Calculate the inspection result
      // Create a map for client decision to inspection result mapping
      const decisionResultMap = new Map<string, InspectionResult>([
        [ClientDecision.FAIL_RTV100, InspectionResult.FAIL],
        [ClientDecision.FAIL_RTV_CANCEL, InspectionResult.FAIL],
        [ClientDecision.PASS_LEVEL1, InspectionResult.PASS],
        [ClientDecision.PASS_LEVEL2, InspectionResult.PASS],
        [ClientDecision.PASS_CPR_LEVEL1, InspectionResult.COMMERCIAL_PASS],
        [ClientDecision.PASS_CPR_LEVEL2, InspectionResult.COMMERCIAL_PASS],
      ]);

      // Check if the client decision has a predefined result
      if (decisionResultMap.has(clientDecision.description)) {
        console.log(
          '[calculateInspectionResult] client decision result ',
          decisionResultMap.get(clientDecision.description)
        );
        return decisionResultMap.get(clientDecision.description) || InspectionResult.PASS;
      }
    }
  }

  //calculate defect count by severity
  // Create a lookup object for defect severities
  const severityLookup = masterData.severities.reduce(
    (lookup, severity) => {
      lookup[severity.name] = severity;
      return lookup;
    },
    {} as Record<string, DefectSeverity>
  );

  // Retrieve severities directly from the lookup
  const critical = severityLookup[DefectSeverityEnum.CRITICAL];
  const major = severityLookup[DefectSeverityEnum.MAJOR];
  const minor = severityLookup[DefectSeverityEnum.MINOR];

  const defectCounts = { critical: 0, major: 0, minor: 0 };

  inspection.qcDefects?.forEach((defect) => {
    if (defect.defectSeverityId === critical.id) {
      defectCounts.critical++;
    } else if (defect.defectSeverityId === major.id) {
      defectCounts.major++;
    } else if (defect.defectSeverityId === minor.id) {
      defectCounts.minor++;
    }
  });
  const completesWithoutVerdict =
    samplingMethod === SamplingMethod.FULL_LOT || samplingMethod === SamplingMethod.FIXED;
  if (
    !completesWithoutVerdict &&
    (defectCounts.critical > 0 ||
      defectCounts.major > inspection.allowedDefectsMaj ||
      defectCounts.minor > inspection.allowedDefectsMin)
  ) {
    return InspectionResult.FAIL;
  }

  // Default to PASS if no other condition matches
  return InspectionResult.PASS;
};
